import {
  ApplicationConfig, ErrorHandler,
  importProvidersFrom,
  isDevMode,
  provideAppInitializer,
  provideExperimentalZonelessChangeDetection,
} from '@angular/core';
import { provideRouter, Router } from '@angular/router';
import { routes } from './app.routes';
import { HttpRequest, provideHttpClient, withInterceptors } from '@angular/common/http';
import { ENV_CONFIG } from './environment.config';
import { environment } from '../environments/environment';
import { provideServiceWorker } from '@angular/service-worker';
import { errorInterceptor } from './core/interceptors/error-interceptor';
import { initializeApp } from './core/services/app-initializer.service';
import { storeImports } from './core/store';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideNgHttpCaching,
  withNgHttpCachingInterceptor,
} from './core/ng-http-caching/ng-http-caching-providers';
import {
  NG_HTTP_CACHING_MONTH_IN_MS,
  NgHttpCachingConfig,
  NgHttpCachingStrategy,
} from './core/ng-http-caching/ng-http-caching.service';
import { NgHttpCachingLocalStorage } from './core/ng-http-caching/storage/ng-http-caching-local-storage';
import { ConnectionServiceModule } from 'ng-connection-service';
import { offlineInterceptor } from './core/interceptors/offline-interceptor';
import { authInterceptor } from './core/interceptors/auth-interceptor';
import { createErrorHandler, TraceService } from "@sentry/angular";
import { additionalEmployeeHeadersInterceptor } from './core/interceptors/additional-employee-headers-interceptor';

// cache config...
const ngHttpCachingConfig: NgHttpCachingConfig = {
  lifetime: NG_HTTP_CACHING_MONTH_IN_MS, // cache expire after 30 days
  cacheStrategy: NgHttpCachingStrategy.DISALLOW_ALL,
  allowedMethod: ['GET', 'POST', 'HEAD'],
  version: '1',
  store: new NgHttpCachingLocalStorage(),
  getKey: (req: HttpRequest<unknown>): string | undefined => {
    // Custom unique key to store the data
    return req.method + '@' + req.urlWithParams;
  },
  isCacheFirst: () => {
    return false;
  },
};

const urlParams = new URLSearchParams(window.location.search)
const isReportBuilderInstance = urlParams.get('reportBuilder') === '1';

export const appConfig: ApplicationConfig = {
  providers: [
    provideExperimentalZonelessChangeDetection(),
    provideRouter(routes),
    provideHttpClient(
      withInterceptors([
        additionalEmployeeHeadersInterceptor,
        authInterceptor,
        errorInterceptor,
        withNgHttpCachingInterceptor(),
        offlineInterceptor,
      ])
    ),
    provideNgHttpCaching(ngHttpCachingConfig),
    {
      provide: ENV_CONFIG,
      useValue: environment,
    },
    {
      provide: ErrorHandler,
      useValue: createErrorHandler(),
    },
    {
      provide: TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {initializeApp(isReportBuilderInstance)}),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode() && !isReportBuilderInstance,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideAnimationsAsync(),
    importProvidersFrom([ConnectionServiceModule]),
    ...storeImports,
  ],
};
